import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Layers, Save, Lock, RefreshCw } from "lucide-react";

const TIER_META = {
  student: { label: "Student & New Grad", monthly: 5, annual: 45, blurb: "For students and grads in their first 2 years" },
  community: { label: "The Community", monthly: 9, annual: 79, blurb: "For every RDH who wants a real community + real tools" },
  hygienist: { label: "The Hygienist", monthly: 17, annual: 149, blurb: "The full toolkit for working RDHs" },
  pro: { label: "RDH Pro", monthly: 29, annual: 249, blurb: "For the hygienist building something bigger" },
};

const TIER_COLORS = {
  student: "#3B82F6",
  community: "#8DB5A0",
  hygienist: "#6B4E8A",
  pro: "#D89B4E",
};

const TierManagementPage = () => {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // {tier: bool}
  // Phase 64.H: tier pricing — editable by founder.
  const [pricing, setPricing] = useState({});
  const [originalPricing, setOriginalPricing] = useState({});
  const [savingPricing, setSavingPricing] = useState({}); // {tier: bool}

  // Founder identity. The actual edit-permission check is enforced server-side.
  const founderEmails = ["ramneetrdh@gmail.com", "admin@dhdh.ca"];
  const isFounder =
    founderEmails.includes((user?.email || "").toLowerCase()) ||
    user?.admin_role === "founder";

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tiers/features`);
      setCatalog(r.data.catalog || []);
      setTiers(r.data.tiers || []);
      setMatrix(JSON.parse(JSON.stringify(r.data.matrix || {})));
      setOriginal(JSON.parse(JSON.stringify(r.data.matrix || {})));
      // Pricing is included on the same payload now (Phase 64.H).
      const p = r.data.pricing || {};
      setPricing(JSON.parse(JSON.stringify(p)));
      setOriginalPricing(JSON.parse(JSON.stringify(p)));
    } catch (e) {
      toast.error("Failed to load tier matrix");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchMatrix(); }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const f of catalog) {
      const g = f.group || "Other";
      out[g] = out[g] || [];
      out[g].push(f);
    }
    return out;
  }, [catalog]);

  const toggle = (tier, featureKey, val) => {
    setMatrix(prev => ({
      ...prev,
      [tier]: { ...(prev[tier] || {}), [featureKey]: val },
    }));
  };

  const isDirty = (tier) =>
    JSON.stringify(matrix[tier] || {}) !== JSON.stringify(original[tier] || {});

  const isPricingDirty = (tier) =>
    JSON.stringify(pricing[tier] || {}) !== JSON.stringify(originalPricing[tier] || {});

  const setPriceField = (tier, key, val) => {
    const num = val === "" ? "" : parseFloat(val);
    setPricing(prev => ({
      ...prev,
      [tier]: { ...(prev[tier] || {}), [key]: Number.isFinite(num) ? num : "" },
    }));
  };

  const savePricing = async (tier) => {
    setSavingPricing(prev => ({ ...prev, [tier]: true }));
    try {
      const fields = pricing[tier] || {};
      const payload = {};
      for (const k of ["monthly", "annual", "founding_monthly", "founding_annual"]) {
        if (fields[k] !== "" && fields[k] !== undefined && fields[k] !== null) {
          payload[k] = fields[k];
        }
      }
      const res = await axios.put(`${API}/tiers/admin/pricing`, { pricing: { [tier]: payload } });
      setPricing(JSON.parse(JSON.stringify(res.data.pricing)));
      setOriginalPricing(JSON.parse(JSON.stringify(res.data.pricing)));
      toast.success(`${TIER_META[tier]?.label || tier} pricing updated — live for new checkouts.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pricing save failed");
    } finally {
      setSavingPricing(prev => ({ ...prev, [tier]: false }));
    }
  };

  const saveTier = async (tier) => {
    setSaving(prev => ({ ...prev, [tier]: true }));
    try {
      const res = await axios.put(`${API}/tiers/features`, {
        matrix: { [tier]: matrix[tier] },
      });
      setMatrix(JSON.parse(JSON.stringify(res.data.matrix)));
      setOriginal(JSON.parse(JSON.stringify(res.data.matrix)));
      toast.success(`${TIER_META[tier]?.label || tier} updated — changes are live for all members.`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Save failed";
      toast.error(detail);
    } finally {
      setSaving(prev => ({ ...prev, [tier]: false }));
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="tier-management-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Layers className="inline w-7 h-7 mr-2 -mt-1 text-[#8DB5A0]" />
              Manage Tiers
            </h1>
            <p className="text-[#64748B] mt-1">
              Toggle which features each membership tier unlocks. Changes go live for all members within ~30 seconds (on next page navigation or window focus).
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isFounder && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-medium">
                <Lock className="w-3.5 h-3.5" />
                Read-only — founder access required to edit
              </span>
            )}
            <Button variant="outline" size="sm" onClick={fetchMatrix} className="rounded-full" data-testid="refresh-matrix-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Reload
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1,2,3,4].map(i => <div key={i} className="h-96 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tiers.map(tierKey => {
              const meta = TIER_META[tierKey] || { label: tierKey };
              const color = TIER_COLORS[tierKey] || "#64748B";
              const dirty = isDirty(tierKey);
              const tierMap = matrix[tierKey] || {};
              const enabledCount = Object.values(tierMap).filter(Boolean).length;
              return (
                <div
                  key={tierKey}
                  className="bg-white border border-[#E5DFEC] rounded-2xl overflow-hidden flex flex-col"
                  data-testid={`tier-box-${tierKey}`}
                >
                  {/* Header */}
                  <div className="p-5 border-b border-[#E5DFEC]" style={{ background: `linear-gradient(135deg, ${color}10, ${color}03)` }}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{meta.label}</h3>
                        <p className="text-xs text-[#64748B] mt-0.5">{meta.blurb}</p>
                        {meta.monthly && (
                          <p className="text-sm text-[#6B4E8A] mt-2 font-medium">
                            <span style={{ color }}>${meta.monthly}/mo</span>
                            <span className="text-[#94A3B8]"> · ${meta.annual}/yr</span>
                          </p>
                        )}
                      </div>
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full" style={{ backgroundColor: `${color}20`, color }}>
                        {enabledCount} features
                      </span>
                    </div>
                  </div>

                  {/* Pricing inputs (Phase 64.H) */}
                  <div className="px-5 pt-4 pb-3 border-b border-[#E5DFEC] bg-[#FDFCFB]" data-testid={`pricing-${tierKey}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">Pricing (CAD)</p>
                      {isPricingDirty(tierKey) && (
                        <span className="text-[10px] font-medium text-[#D97706]">Unsaved</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <label className="text-[10px] text-[#64748B]">
                        Standard $ / month
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="9999"
                          value={(pricing[tierKey]?.monthly ?? "")}
                          onChange={(e) => setPriceField(tierKey, "monthly", e.target.value)}
                          disabled={!isFounder}
                          className="w-full mt-0.5 px-2 py-1 border border-[#E5DFEC] rounded-md text-sm text-[#6B4E8A] bg-white disabled:bg-[#F1F5F9]"
                          data-testid={`price-monthly-${tierKey}`}
                        />
                      </label>
                      <label className="text-[10px] text-[#64748B]">
                        Standard $ / year
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="9999"
                          value={(pricing[tierKey]?.annual ?? "")}
                          onChange={(e) => setPriceField(tierKey, "annual", e.target.value)}
                          disabled={!isFounder}
                          className="w-full mt-0.5 px-2 py-1 border border-[#E5DFEC] rounded-md text-sm text-[#6B4E8A] bg-white disabled:bg-[#F1F5F9]"
                          data-testid={`price-annual-${tierKey}`}
                        />
                      </label>
                      <label className="text-[10px] text-[#8DB5A0] font-medium">
                        Founding $ / month
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="9999"
                          value={(pricing[tierKey]?.founding_monthly ?? "")}
                          onChange={(e) => setPriceField(tierKey, "founding_monthly", e.target.value)}
                          disabled={!isFounder}
                          className="w-full mt-0.5 px-2 py-1 border border-[#8DB5A0]/40 rounded-md text-sm text-[#6B4E8A] bg-white disabled:bg-[#F1F5F9]"
                          data-testid={`price-founding-monthly-${tierKey}`}
                        />
                      </label>
                      <label className="text-[10px] text-[#8DB5A0] font-medium">
                        Founding $ / year
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="9999"
                          value={(pricing[tierKey]?.founding_annual ?? "")}
                          onChange={(e) => setPriceField(tierKey, "founding_annual", e.target.value)}
                          disabled={!isFounder}
                          className="w-full mt-0.5 px-2 py-1 border border-[#8DB5A0]/40 rounded-md text-sm text-[#6B4E8A] bg-white disabled:bg-[#F1F5F9]"
                          data-testid={`price-founding-annual-${tierKey}`}
                        />
                      </label>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => savePricing(tierKey)}
                        disabled={!isFounder || !isPricingDirty(tierKey) || savingPricing[tierKey]}
                        className="rounded-full text-xs h-7"
                        data-testid={`save-pricing-${tierKey}-btn`}
                      >
                        {savingPricing[tierKey] ? "Saving…" : "Save pricing"}
                      </Button>
                    </div>
                  </div>

                  {/* Feature checklist */}
                  <div className="flex-1 p-5 space-y-4 max-h-[55vh] overflow-y-auto">
                    {Object.entries(grouped).map(([group, items]) => (
                      <div key={group}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">{group}</p>
                        <div className="space-y-1.5">
                          {items.map(f => {
                            const checked = !!tierMap[f.key];
                            return (
                              <label
                                key={f.key}
                                htmlFor={`tier-${tierKey}-${f.key}`}
                                className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors ${
                                  isFounder ? "hover:bg-[#FAF7F2]" : "cursor-default opacity-90"
                                }`}
                                data-testid={`feature-toggle-${tierKey}-${f.key}`}
                              >
                                <Checkbox
                                  id={`tier-${tierKey}-${f.key}`}
                                  checked={checked}
                                  disabled={!isFounder}
                                  onCheckedChange={(v) => toggle(tierKey, f.key, !!v)}
                                />
                                <span className={`text-sm ${checked ? 'text-[#6B4E8A] font-medium' : 'text-[#64748B]'}`}>
                                  {f.label}
                                  {f.is_cosmetic && (
                                    <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[#94A3B8] font-medium" title="Toggling this feature today affects the public landing copy but not raw API access. Server-side enforcement coming in a future sprint.">
                                      copy-only
                                    </span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer / Save */}
                  <div className="p-4 border-t border-[#E5DFEC] flex items-center justify-between bg-[#FAF7F2]">
                    <span className="text-xs text-[#64748B]">
                      {dirty ? (
                        <span className="text-[#D97706] font-medium">Unsaved changes</span>
                      ) : (
                        "All saved"
                      )}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => saveTier(tierKey)}
                      disabled={!isFounder || !dirty || saving[tierKey]}
                      className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                      data-testid={`save-tier-${tierKey}-btn`}
                    >
                      {saving[tierKey] ? "Saving..." : <><Save className="w-3.5 h-3.5 mr-1" /> Save Changes</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TierManagementPage;
